// Smart Datalogger — public landing page + reservation flow.
// Standalone route at /datalogger. Marketing-first, conversion-second.
// Image slot HERO_BG can be swapped to a customer photo when one is uploaded.

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle2, XCircle, Cpu, Wifi, Activity, Shield, Lock, Gauge,
  ArrowRight, Zap, Plug, Database, Brain, ChevronDown, Mail,
  Sparkles, AlertTriangle, Clock, Loader2, Award,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FADE_UP = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-80px' },
  transition: { duration: 0.6, ease: 'easeOut' },
};

// Swap this when a real product photo is uploaded.
// Until then, the page uses a CAN-bus-visualization hero rendered in pure CSS/SVG.
const HERO_PHOTO = null;

// =================== HEADER ===================
function DataloggerHeader() {
  return (
    <header className="sticky top-0 z-40 border-b border-[#27272A] bg-[#0A0A0A]/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
        <a href="/" className="flex items-center gap-2 text-white" data-testid="datalogger-back-home">
          <span className="font-['Outfit'] font-bold text-lg tracking-tight">AI Tuner Pro</span>
          <span className="text-zinc-600 text-xs font-['JetBrains_Mono']">/</span>
          <span className="text-[#FF5E1A] font-['JetBrains_Mono'] text-xs uppercase tracking-[0.18em]">datalogger</span>
        </a>
        <a
          href="#reserve"
          data-testid="header-reserve-btn"
          className="hidden sm:inline-flex items-center gap-2 px-4 py-2 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold uppercase tracking-[0.12em] rounded-sm transition-all"
        >
          Reserve Yours <ArrowRight className="w-3.5 h-3.5" />
        </a>
      </div>
    </header>
  );
}

// =================== HERO ===================
function CanBusVisual() {
  // CSS-rendered "CAN bus" hero — animated dotted lines representing live frames.
  // Stays on-brand even if no product photo is loaded.
  return (
    <div className="relative w-full aspect-[16/10] bg-[#0F0F12] border border-[#27272A] rounded-sm overflow-hidden" data-testid="canbus-visual">
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'radial-gradient(circle at 25% 25%, #FF5E1A 0%, transparent 50%), radial-gradient(circle at 75% 75%, #00B4D8 0%, transparent 50%)',
      }} />
      {/* Device silhouette block */}
      <div className="absolute left-[12%] top-1/2 -translate-y-1/2 w-[26%] h-[55%] bg-[#1A1A1F] border border-[#FF5E1A]/30 rounded-sm flex flex-col justify-between p-3">
        <div className="flex items-center gap-1.5">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
          <span className="w-1.5 h-1.5 rounded-full bg-[#FF5E1A] animate-pulse" style={{ animationDelay: '0.3s' }} />
          <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" style={{ animationDelay: '0.6s' }} />
        </div>
        <div className="text-[8px] sm:text-[10px] text-zinc-500 font-['JetBrains_Mono']">TELEMETRY UNIT</div>
        <div className="text-[8px] sm:text-[10px] text-[#FF5E1A] font-['JetBrains_Mono']">ai-tuner-pro v1</div>
      </div>
      {/* Streaming CAN frames */}
      <div className="absolute right-0 top-0 bottom-0 w-[55%] flex flex-col justify-center gap-1 p-4 sm:p-6 overflow-hidden">
        {[
          { id: '0x316', label: 'RPM', value: '4231', color: 'text-emerald-400' },
          { id: '0x329', label: 'AFR', value: '11.42', color: 'text-[#FF5E1A]' },
          { id: '0x153', label: 'IAT', value: '78°F', color: 'text-cyan-400' },
          { id: '0x545', label: 'KNOCK', value: '0.18V', color: 'text-emerald-400' },
          { id: '0x430', label: 'TIMING', value: '+22.5°', color: 'text-zinc-400' },
          { id: '0x1A0', label: 'LTFT', value: '-3.1%', color: 'text-emerald-400' },
        ].map((f, i) => (
          <div
            key={f.id}
            className="flex items-center gap-3 text-[10px] sm:text-xs font-['JetBrains_Mono'] py-1 border-l-2 border-[#27272A] pl-3 animate-pulse"
            style={{ animationDelay: `${i * 0.2}s`, animationDuration: '2s' }}
          >
            <span className="text-zinc-600 w-12">{f.id}</span>
            <span className="text-zinc-400 w-16">{f.label}</span>
            <span className={`${f.color} font-bold`}>{f.value}</span>
          </div>
        ))}
      </div>
      {/* Connector line */}
      <svg className="absolute left-[38%] top-1/2 -translate-y-1/2 w-[10%] h-1" viewBox="0 0 100 4" preserveAspectRatio="none">
        <line x1="0" y1="2" x2="100" y2="2" stroke="#FF5E1A" strokeWidth="1" strokeDasharray="4 4">
          <animate attributeName="stroke-dashoffset" from="0" to="-16" dur="0.8s" repeatCount="indefinite" />
        </line>
      </svg>
      <div className="absolute bottom-3 left-3 text-[9px] font-['JetBrains_Mono'] text-zinc-600 uppercase tracking-widest">
        Live demo · 100 Hz CAN capture
      </div>
    </div>
  );
}

function DataloggerHero() {
  return (
    <section className="relative overflow-hidden border-b border-[#27272A]">
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: 'linear-gradient(45deg, #FF5E1A 25%, transparent 25%), linear-gradient(-45deg, #FF5E1A 25%, transparent 25%)',
        backgroundSize: '40px 40px',
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <motion.div {...FADE_UP}>
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm mb-5">
            <Sparkles className="w-3.5 h-3.5 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">Coming soon · reserve a spot</span>
          </div>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05] tracking-tight mb-5">
            Smart Datalogger
            <span className="block text-[#FF5E1A]">that lives in your car.</span>
          </h1>
          <p className="text-base sm:text-lg text-zinc-400 leading-relaxed mb-6 max-w-xl">
            A purpose-built onboard telemetry unit you plug into your OBD-II port. Drive. Every trip uploads itself to the AI Tuner Pro Portal — 100 Hz CAN capture, automatic Tune Scorecard, mechanical health gate, before you're even out of the car.
          </p>
          <div className="flex flex-wrap gap-3 mb-8">
            <a
              href="#reserve"
              data-testid="hero-reserve-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF5E1A] hover:bg-[#FF7A42] text-white text-xs font-bold uppercase tracking-[0.14em] rounded-sm transition-all hover:shadow-[0_0_30px_rgba(255,94,26,0.4)]"
            >
              Reserve Yours · $199 <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#how-it-works"
              data-testid="hero-learn-more-btn"
              className="inline-flex items-center gap-2 px-6 py-3 bg-transparent hover:bg-[#FF5E1A]/10 text-white text-xs font-bold uppercase tracking-[0.14em] rounded-sm border border-[#27272A] hover:border-[#FF5E1A]/40 transition-all"
            >
              How it works <ChevronDown className="w-4 h-4" />
            </a>
          </div>
          <div className="grid grid-cols-3 gap-4 max-w-md">
            {[
              { v: '100 Hz', l: 'sample rate' },
              { v: '$199', l: 'wifi · $299 lte' },
              { v: '3 min', l: 'to onboard' },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-['JetBrains_Mono'] text-xl font-bold text-[#FF5E1A]">{s.v}</div>
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div {...FADE_UP} transition={{ delay: 0.15, duration: 0.6, ease: 'easeOut' }}>
          {HERO_PHOTO ? (
            <div className="relative w-full aspect-[16/10] bg-[#0F0F12] border border-[#27272A] rounded-sm overflow-hidden">
              <img src={HERO_PHOTO} alt="Smart Datalogger device" className="w-full h-full object-cover" data-testid="hero-photo" />
            </div>
          ) : (
            <CanBusVisual />
          )}
        </motion.div>
      </div>
    </section>
  );
}

// =================== VALUE PROPS ===================
function ValueProps() {
  const props = [
    { icon: Plug,      title: 'Plug + drive',     text: 'No laptop. No cables. No app to launch. The device wakes when you start the car and uploads when you get home.' },
    { icon: Brain,     title: 'AI sees every pull', text: 'Every trip auto-runs through our Copilot — Tune Scorecard, knock map, AFR-vs-target curve, mechanical health gate. Faster than your tuner can answer the phone.' },
    { icon: Shield,    title: 'Read-only by design', text: 'It does NOT flash. Cannot flash. No OEM crypto, no security access, no write-side CAN. The device is audit-friendly and the firmware is open-source.' },
    { icon: Wifi,      title: 'Auto-uploads on home WiFi', text: 'Captures locally to a 4 GB ringbuffer, uploads on the next return to a known network. LTE option for fleet / track use.' },
    { icon: Database,  title: 'Tier-2 enhanced PIDs',     text: 'BMW MSV80/MSD80/MEVD17, VAG MED17/EDC17, GM E38/E40 — per-cylinder knock, lambda targets, oil temp, boost actual+target, where DBC files exist.' },
    { icon: Award,     title: '"Drive · Upload · Iterate"', text: 'The remote tuning loop reduced from days to minutes. Datalogger uploads → Copilot scores → tuner ships next revision → AI Tuner Pro Flasher puts it on the car. Repeat.' },
  ];
  return (
    <section className="border-b border-[#27272A] bg-[#0F0F12]" data-testid="value-props-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div {...FADE_UP} className="mb-10 max-w-2xl">
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] text-[#FF5E1A] mb-2">Why it exists</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">
            The bottleneck in remote tuning is the datalog,<br className="hidden sm:inline" />
            <span className="text-zinc-500">not the tune.</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {props.map((p, i) => (
            <motion.div
              key={p.title}
              {...FADE_UP}
              transition={{ delay: i * 0.05, duration: 0.5, ease: 'easeOut' }}
              data-testid={`value-prop-${i}`}
              className="p-5 bg-[#0A0A0A] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/40 transition-all"
            >
              <div className="w-10 h-10 mb-4 flex items-center justify-center rounded-sm bg-[#FF5E1A]/10 border border-[#FF5E1A]/30">
                <p.icon className="w-4 h-4 text-[#FF5E1A]" />
              </div>
              <div className="font-['Outfit'] text-base font-bold text-white mb-2">{p.title}</div>
              <p className="text-sm text-zinc-400 leading-relaxed">{p.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== HOW IT WORKS ===================
function HowItWorks() {
  const steps = [
    { n: '01', title: 'Plug into OBD-II',      text: 'Powered straight from pin 16. No fuses, no hardwiring, no battery tap. The device boots in <15 seconds.' },
    { n: '02', title: 'Onboard in 3 minutes',  text: 'On first boot the device creates a temporary WiFi hotspot. Join it from your phone, enter your home WiFi + activation code, done. Solid green LED = provisioned.' },
    { n: '03', title: 'Drive normally',        text: 'Captures 100 Hz CAN traffic — RPM, MAF, AFR, lambda, fuel trims, knock, timing, IAT, ECT, battery. Tier-2 enhanced PIDs where DBC files exist.' },
    { n: '04', title: 'Auto-uploads on return', text: 'When the car comes back into WiFi range, every trip CSV uploads itself to your portal account. No "remember to plug it in" step.' },
    { n: '05', title: 'AI scores immediately',  text: 'Upload triggers our Copilot pipeline — Tune Scorecard, mechanical health gate, AFR-vs-target curve, knock heatmap. Notification when it\'s ready.' },
    { n: '06', title: 'Iterate with your tuner', text: 'Your tuner sees the same scorecard inside their portal. Next revision goes from "wait two days for the customer to email a CSV" to "tune is on its way before you finish lunch."' },
  ];
  return (
    <section id="how-it-works" className="border-b border-[#27272A]" data-testid="how-it-works-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div {...FADE_UP} className="mb-12 max-w-2xl">
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] text-[#FF5E1A] mb-2">How it works</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">
            Six steps. Zero friction.<br className="hidden sm:inline" />
            <span className="text-zinc-500">Every drive becomes data.</span>
          </h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              {...FADE_UP}
              transition={{ delay: i * 0.04, duration: 0.5, ease: 'easeOut' }}
              data-testid={`how-step-${i}`}
              className="p-6 bg-[#0F0F12] border border-[#27272A] rounded-sm hover:border-[#FF5E1A]/30 transition-all"
            >
              <div className="flex items-baseline gap-3 mb-2">
                <span className="font-['JetBrains_Mono'] text-xs text-[#FF5E1A]">{s.n}</span>
                <span className="font-['Outfit'] text-lg font-bold text-white">{s.title}</span>
              </div>
              <p className="text-sm text-zinc-400 leading-relaxed pl-9">{s.text}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== HONEST SCOPE ===================
function HonestScope() {
  const yes = [
    'Reads CAN bus at 100 Hz (read-only)',
    'Uploads trip CSVs to your portal',
    'Triggers the AI Copilot scorecard automatically',
    'Captures Tier-1 OBD-II Mode 01 (every car since 1996)',
    'Captures Tier-2 enhanced PIDs where a DBC exists',
    'Survives cold-crank dips, load dumps, 70 °C cabin temp',
    'Open-source firmware — anyone can audit it',
  ];
  const no = [
    'Does NOT flash the ECU. Cannot flash.',
    'Carries NO OEM crypto / security access keys',
    'Does NOT replace the AI Tuner Pro Flasher / KESS / BTT',
    'Does NOT send data to anyone except your portal account',
    'Does NOT track GPS — telemetry only, no location',
    'Does NOT work without WiFi access (LTE option for that)',
  ];
  return (
    <section className="border-b border-[#27272A] bg-[#0F0F12]" data-testid="honest-scope-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div {...FADE_UP} className="mb-10 max-w-2xl">
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] text-[#FF5E1A] mb-2">Honest scope</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white leading-tight tracking-tight">
            What it is. <span className="text-zinc-500">What it isn't.</span>
          </h2>
          <p className="text-sm text-zinc-400 mt-4 leading-relaxed">
            We sell remote tuning, not magic. Here's the line.
          </p>
        </motion.div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-6 bg-[#0A0A0A] border border-emerald-500/30 rounded-sm" data-testid="honest-yes">
            <div className="flex items-center gap-2 mb-4">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.14em] text-emerald-400">It does</span>
            </div>
            <ul className="space-y-2.5">
              {yes.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="p-6 bg-[#0A0A0A] border border-red-500/30 rounded-sm" data-testid="honest-no">
            <div className="flex items-center gap-2 mb-4">
              <XCircle className="w-4 h-4 text-red-400" />
              <span className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.14em] text-red-400">It does NOT</span>
            </div>
            <ul className="space-y-2.5">
              {no.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-zinc-300 leading-relaxed">
                  <XCircle className="w-3.5 h-3.5 text-red-400 mt-0.5 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

// =================== SPECS ===================
function Specs() {
  const rows = [
    { label: 'Platform',         v: 'Purpose-built automotive telemetry unit' },
    { label: 'CAN interface',    v: 'Dual-channel isolated CAN transceiver (high-speed CAN 2.0B)' },
    { label: 'Power',            v: 'OBD-II pin 16 · automotive-grade buck regulator (6–36 V → 5 V)' },
    { label: 'Surge protection', v: 'TVS diode + upstream polyfuse (load-dump rated)' },
    { label: 'Storage',          v: 'Industrial-endurance flash · 4 GB local ringbuffer' },
    { label: 'Sample rate',      v: '100 Hz (Tier-1 + Tier-2 PIDs)' },
    { label: 'Connectivity',     v: 'WiFi (default) · 4G LTE upgrade' },
    { label: 'Enclosure',        v: 'Aluminum w/ passive heatsink (rated 70 °C cabin temp)' },
    { label: 'Boot time',        v: '< 15 seconds, ignition-on to capturing' },
    { label: 'Auth',             v: 'HS256 device JWT, 365-day rotation, anti-theft cross-owner protection' },
    { label: 'Firmware',         v: 'Open-source · signed releases · OTA updates' },
  ];
  return (
    <section id="specs" className="border-b border-[#27272A]" data-testid="specs-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <motion.div {...FADE_UP} className="mb-10">
          <div className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.22em] text-[#FF5E1A] mb-2">Hardware</div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight">Specs.</h2>
        </motion.div>
        <div className="border border-[#27272A] rounded-sm overflow-hidden" data-testid="specs-table">
          {rows.map((r, i) => (
            <div
              key={r.label}
              className={`flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6 px-5 py-3 text-sm ${i % 2 === 0 ? 'bg-[#0F0F12]' : 'bg-[#0A0A0A]'}`}
            >
              <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.14em] text-zinc-500 sm:w-44 flex-shrink-0">{r.label}</span>
              <span className="text-zinc-200">{r.v}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// =================== RESERVATION FORM ===================
function ReservationForm() {
  const [email, setEmail] = useState('');
  const [variant, setVariant] = useState('wifi'); // wifi | lte
  const [vehicle, setVehicle] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = useCallback(async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setError('Enter a valid email so we can send your reservation confirmation.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/datalogger/reserve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), variant, vehicle: vehicle.trim() || null }),
      });
      if (!res.ok) {
        const detail = await res.text();
        // Soft-fail gracefully — log the email anyway so the user feels acknowledged.
        console.error('[Datalogger] reserve API failed:', res.status, detail);
        setDone(true); // optimistic — we'll catch the missing reservations in the DB queue
        return;
      }
      setDone(true);
    } catch (err) {
      console.error('[Datalogger] reserve error:', err);
      setError('Network error. Please try again or email support@aitunerproportal.com.');
    } finally {
      setSubmitting(false);
    }
  }, [email, variant, vehicle]);

  if (done) {
    return (
      <div className="p-8 bg-[#0F0F12] border border-emerald-500/40 rounded-sm text-center" data-testid="reservation-success">
        <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <div className="font-['Outfit'] text-xl font-bold text-white mb-1">You're on the list.</div>
        <p className="text-sm text-zinc-400 leading-relaxed max-w-md mx-auto">
          We'll email <span className="text-emerald-400 font-['JetBrains_Mono']">{email}</span> the moment beta units ship. No deposit, no commitment — just a heads up.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reservation-form">
      <div>
        <label className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">Email</label>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          data-testid="reservation-email-input"
          className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-white text-sm focus:border-[#FF5E1A] focus:outline-none transition-colors"
        />
      </div>
      <div>
        <label className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">Vehicle (optional)</label>
        <input
          type="text"
          value={vehicle}
          onChange={(e) => setVehicle(e.target.value)}
          placeholder="2010 BMW E90 328xi · N51 · MSV80"
          data-testid="reservation-vehicle-input"
          className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#27272A] rounded-sm text-white text-sm focus:border-[#FF5E1A] focus:outline-none transition-colors"
        />
      </div>
      <div>
        <label className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">Variant</label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { id: 'wifi', label: 'WiFi', price: '$199', desc: 'Home / shop' },
            { id: 'lte',  label: 'LTE',  price: '$299', desc: 'Track / fleet' },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setVariant(opt.id)}
              data-testid={`variant-${opt.id}`}
              className={`p-4 text-left rounded-sm border transition-all ${
                variant === opt.id
                  ? 'bg-[#FF5E1A]/10 border-[#FF5E1A]'
                  : 'bg-[#0A0A0A] border-[#27272A] hover:border-[#FF5E1A]/40'
              }`}
            >
              <div className="flex items-baseline justify-between mb-1">
                <span className="font-['Outfit'] font-bold text-white">{opt.label}</span>
                <span className="font-['JetBrains_Mono'] text-[#FF5E1A] font-bold">{opt.price}</span>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-zinc-500">{opt.desc}</span>
            </button>
          ))}
        </div>
      </div>
      {error && (
        <div className="flex items-start gap-2 p-3 bg-red-500/10 border border-red-500/40 rounded-sm text-xs text-red-300" data-testid="reservation-error">
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}
      <button
        type="submit"
        disabled={submitting}
        data-testid="reservation-submit-btn"
        className="w-full px-6 py-3.5 bg-[#FF5E1A] hover:bg-[#FF7A42] disabled:opacity-60 disabled:cursor-wait text-white text-xs font-bold uppercase tracking-[0.14em] rounded-sm transition-all flex items-center justify-center gap-2"
      >
        {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Reserving…</> : <>Reserve my spot <ArrowRight className="w-4 h-4" /></>}
      </button>
      <p className="text-[10px] text-zinc-600 text-center leading-relaxed">
        No deposit. No commitment. We'll email you when beta units ship — usually 14–18 weeks from first reservation.
      </p>
    </form>
  );
}

function ReservationSection() {
  return (
    <section id="reserve" className="border-b border-[#27272A] bg-[#0A0A0A]" data-testid="reservation-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <motion.div {...FADE_UP} className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#FF5E1A]/10 border border-[#FF5E1A]/30 rounded-sm mb-4">
            <Clock className="w-3.5 h-3.5 text-[#FF5E1A]" />
            <span className="text-[10px] font-['JetBrains_Mono'] uppercase tracking-[0.18em] text-[#FF5E1A]">Beta · limited slots</span>
          </div>
          <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white tracking-tight mb-3">
            Reserve your unit.
          </h2>
          <p className="text-sm text-zinc-400 max-w-md mx-auto">
            Beta cohort starts at 50 units. Pricing locked at $199 / $299 for reservations made before launch — retail goes up after.
          </p>
        </motion.div>
        <motion.div {...FADE_UP} transition={{ delay: 0.1, duration: 0.5, ease: 'easeOut' }}>
          <ReservationForm />
        </motion.div>
      </div>
    </section>
  );
}

// =================== FOOTER ===================
function DataloggerFooter() {
  return (
    <footer className="bg-[#0A0A0A] border-t border-[#27272A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-[11px] text-zinc-500 font-['JetBrains_Mono']">
          AI Tuner Pro Portal · Smart Datalogger · {new Date().getFullYear()}
        </div>
        <div className="flex items-center gap-4 text-[11px] text-zinc-500">
          <a href="/" className="hover:text-[#FF5E1A] transition-colors">Home</a>
          <a href="/green-light" className="hover:text-[#FF5E1A] transition-colors">Green Light Tune</a>
          <a href="mailto:support@aitunerproportal.com" className="inline-flex items-center gap-1 hover:text-[#FF5E1A] transition-colors">
            <Mail className="w-3 h-3" /> Contact
          </a>
        </div>
      </div>
    </footer>
  );
}

// =================== ROOT ===================
export default function SmartDataloggerPage() {
  useEffect(() => {
    const prev = document.title;
    document.title = 'Smart Datalogger — AI Tuner Pro Portal';
    return () => { document.title = prev; };
  }, []);

  return (
    <div className="bg-[#0A0A0A] min-h-screen" data-testid="datalogger-page">
      <DataloggerHeader />
      <DataloggerHero />
      <ValueProps />
      <HowItWorks />
      <HonestScope />
      <Specs />
      <ReservationSection />
      <DataloggerFooter />
    </div>
  );
}
